/**
 * Context Kinds are billed per unique context key. Only use context keys that are reasonable unique keys.
 *
 * For example, user IDs are not valid keys. Team is likely the most unique key we can tolerate.
 */
export enum FeatureFlagContextKind {
  TEAM = "team",
  SEARCH_INDEX = "search-index",
  SYSTEM = "system",
  USER = "user", // WARNING: This is an expensive context kind. Be sure you actually want to use it.
}

export enum FeatureFlagKey {
  RELEASE_L3_CHARGE_METADATA = "release-l-3-charge-metadata", // 19 May 2025 https://app.launchdarkly.com/projects/default/flags/release-l-3-charge-metadata/targeting?env=test&selected-env=test
  RELEASE_MARKETING_SUBSCRIPTION_QUEUE = "release-marketing-subscription-queue",
  RELEASE_OT_RECIPIENT_SENDING = "release-ot-recipient-sending",
  USER_PERMISSIONS = "user-permissions",
  RELEASE_TEMPORAL_PROCESSING_MARK_AS_PROCESSED = "release-temporal-processing-mark-as-processed", //https://app.launchdarkly.com/projects/default/flags/release-temporal-processing-mark-as-processed/targeting?env=test&selected-env=test
  RELEASE_TEMPORAL_PROCESSING_EFFECTS_PART_ONE = "release-temporal-processing-effects-part-one",
  RELEASE_TEMPORAL_PROCESSING_EFFECTS_PART_TWO = "release-temporal-processing-effects-part-two",
  RELEASE_TEMPORAL_PROCESSING_EFFECTS_PART_THREE = "release-temporal-processing-effects-part-three",
  USE_OPENAI_FALLBACK = "use-openai-fallback", // Fallback from OpenAI to another provider during outages
  CREATE_DRAFT_ORDER_GRAPH_QL = "create-draft-order-graph-ql", // Use GraphQL API instead of REST API for draft order creation
  STOREFRONT_CHECKOUT_PROBABILITY_CALCULATION = "storefront-checkout-probability-calculation", // Enable checkout probability tracking in Shopify extension
  OPENSEARCH_INDEXES = "open-search-indexes", // Control which OpenSearch indexes are enabled per team
  RELEASE_EXCLUDE_MANUAL_TRANSACTIONS_FROM_REFUNDS = "release-exclude-manual-transactions-from-refunds", // Exclude manual payment gateway transactions from refunds
  UPDATE_CONVERSATION_LOGGING = "update-conversation-logging", // Enable detailed logging for update conversation operations for support
  MARKETING_AGENT_SUPPORT = "marketing-agent-support", // Enable marketing agent in merchant support triage
  MERCHANT_ONBOARDING = "merchant-onboarding", // Merchant onboarding flow: "none" (default) or "guided-available" (optional) or "guided" (redirect to guided onboarding)
  PLATFORM_AGENT_SUPPORT = "platform-agent-support", // Enable platform documentation agent in merchant support triage
  ANALYTICS_INSIGHTS_TAB = "analytics-insights-tab", // Enable insights tab in analytics
  OMS_AGENT_SUPPORT = "oms-agent-support", // Enable OMS agent in merchant support triage
  SES_EMAIL_TRACKING_PIXEL_AT_TOP = "ses-email-tracking-pixel-at-top", // Move SES tracking pixel to top of emails
  ORDERS_API_INTEGRATION = "orders-api-integration", // Enable Orders API integration for external order tracking
  NEW_EXCHANGE_REFUND_FLOW = "new-exchange-flow", // Enable new return flow to drive exchanges
  AUTOMATIONS_SEND_WEBHOOK_ENABLED = "automations-send-webhook-enabled", // Enable send webhook step in marketing automations
  RETURN_ACCOUNTING_CALCULATOR = "return-accounting-calculator", // Enable return accounting calculator
  CUSTOMER_EVENTS_CLUSTER = "customer-events-cluster", // Enable mongodb customer events cluster
  CONTINUE_VS_EXCHANGE = "continue-vs-exchange-wording", // Use the words "continue with recommended exchange" instead of just "continue"
  SHOPIFY_LEAKY_BUCKET_LIMITING = "shopify-leaky-bucket-limiting", // Use internal leaky bucket limiting for Shopify GraphQL calls
}

export enum FeatureFlagNumericMetricKey {
  EXCHANGE_REQUEST_SUBMITTED = "exchange-request-submitted",
  EXCHANGE_AGENT_EXCHANGE_REQUEST = "exchange-agent-exchange-request",
}
